import { useEffect, useId, useState } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, X } from 'lucide-react';
import { api, ApiError } from '../../lib/api';
import { useT } from '../../i18n';
import type { DealParams, DealPosition, Inquiry } from '../../types/api';
import {
  dealParamsDraftError,
  dealParamsEqual,
  dealParamsIsEmpty,
  dealSumMismatch,
  draftFromStored,
  positionLabels,
  positionsTotalCount,
  type DealParamsDraftError,
} from '../../lib/dealParams';
import { formatDate, formatMoney } from '../../lib/format';
import { Button } from '../Button';
import { Input } from '../Input';
import { useToast } from '../Toast';
import styles from './DealParamsSection.module.css';

export interface DealParamsSectionProps {
  inquiry: Pick<Inquiry, 'id' | 'productType' | 'dealParams'>;
  /** Only the manufacturer edits deal parameters; integrators read. */
  canEdit?: boolean;
}

/** The PATCH replaces the block as a whole — trim labels into the canonical
 *  shape before sending (the server trims/re-validates anyway). */
function canonicalPayload(d: DealParams): DealParams {
  return {
    positions: d.positions.map((p) => ({ robotType: p.robotType.trim(), quantity: p.quantity })),
    discountMode: d.discountMode,
    totalContractCost: d.totalContractCost,
    robotCost: d.robotCost,
    rtcCost: d.rtcCost,
    otherCost: d.otherCost,
    payments: d.payments.map((p) => ({ amount: p.amount, paidAt: p.paidAt })),
    shipment: d.shipment.map((s) => ({ positionLabel: s.positionLabel.trim(), shipAt: s.shipAt })),
    integrationDates: [...d.integrationDates],
    deliveryDate: d.deliveryDate,
    closingAt: d.closingAt,
  };
}

/** Number-input value: empty string stays empty (null/NaN never renders). */
function numValue(v: number | null | undefined): number | '' {
  return v == null || Number.isNaN(v) ? '' : v;
}

/** «Плашка» (item 2): grey and un-picked by default; click opens the two
 *  display choices. Picking sets discountMode; clicking again re-opens. */
function DiscountPlate({
  mode,
  disabled,
  onPick,
}: {
  mode: DealParams['discountMode'];
  disabled: boolean;
  onPick: (mode: NonNullable<DealParams['discountMode']>) => void;
}) {
  const { t } = useT();
  const [open, setOpen] = useState(false);
  return (
    <div className={styles.plateWrap}>
      <button
        type="button"
        className={mode === null ? styles.plateIdle : styles.plateActive}
        aria-haspopup="menu"
        aria-expanded={open}
        disabled={disabled}
        onClick={() => setOpen((v) => !v)}
      >
        {mode === null
          ? t('dealParams.plateIdle')
          : t(mode === 'DISCOUNT' ? 'dealParams.modeDiscount' : 'dealParams.modeFullPrice')}
      </button>
      {open && (
        <div role="menu" aria-label={t('dealParams.plateChange')} className={styles.plateMenu}>
          <button
            type="button"
            role="menuitem"
            className={styles.plateOption}
            onClick={() => {
              onPick('DISCOUNT');
              setOpen(false);
            }}
          >
            {t('dealParams.modeDiscount')}
          </button>
          <button
            type="button"
            role="menuitem"
            className={styles.plateOption}
            onClick={() => {
              onPick('FULL_PRICE');
              setOpen(false);
            }}
          >
            {t('dealParams.modeFullPrice')}
          </button>
        </div>
      )}
    </div>
  );
}

/**
 * Issue #80 (slice 2) — the «Параметры сделки» block of the «Контракт» step
 * (the page gates it by stage — see stageShowsDealParams). Industrial deals
 * get the full cost breakdown (Робот / РТК / иные услуги + аванс/оплата rows
 * + the live 4.1 sums hint); the educational variant stays simple per the
 * customer wording — total, positions count, delivery date. Saving is an
 * explicit «Сохранить» (same UX as the «Генподрядчик» block, #78); the draft
 * resets per opened card, not on background refetches.
 */
export function DealParamsSection({ inquiry, canEdit = false }: DealParamsSectionProps) {
  const { t, locale } = useT();
  const toast = useToast();
  const queryClient = useQueryClient();
  const listId = useId();
  const educational = inquiry.productType === 'EDUCATIONAL_ROBOT';
  const stored = inquiry.dealParams ?? null;

  const [draft, setDraft] = useState<DealParams>(() => draftFromStored(stored));
  const [formError, setFormError] = useState<DealParamsDraftError | null>(null);
  useEffect(() => {
    setDraft(draftFromStored(inquiry.dealParams ?? null));
    setFormError(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps -- per-card reset, like #78
  }, [inquiry.id]);

  const mutation = useMutation({
    mutationFn: (value: DealParams) =>
      api.patch<Inquiry>(`/inquiries/${inquiry.id}`, { dealParams: value }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['inquiries'] });
      toast.success(t('inquiries.toastUpdated'));
    },
    onError: (err) => {
      const msg = err instanceof ApiError ? err.message : undefined;
      toast.error(t('inquiries.toastUpdateFail'), msg);
    },
  });

  const dirty = !dealParamsEqual(draft, draftFromStored(stored));

  const save = () => {
    if (mutation.isPending) return;
    const err = dealParamsDraftError(draft);
    if (err) {
      setFormError(err);
      return;
    }
    setFormError(null);
    mutation.mutate(canonicalPayload(draft));
  };

  // ── Draft row helpers ────────────────────────────────────────────
  const patch = (p: Partial<DealParams>) => setDraft((d) => ({ ...d, ...p }));
  const patchPosition = (i: number, p: Partial<DealPosition>) =>
    setDraft((d) => ({
      ...d,
      positions: d.positions.map((row, j) => (j === i ? { ...row, ...p } : row)),
    }));

  const mismatch = dealSumMismatch(draft);
  const breakdownEntered =
    draft.robotCost != null || draft.rtcCost != null || draft.otherCost != null;
  const showSumHint = !educational && draft.totalContractCost != null && breakdownEntered;

  // ── Read-only digest (integrators) ───────────────────────────────
  if (!canEdit) {
    return (
      <section aria-label={t('dealParams.title')} className={styles.section}>
        <h3 className={styles.title}>{t('dealParams.title')}</h3>
        {stored === null || dealParamsIsEmpty(draftFromStored(stored)) ? (
          <p className={styles.empty}>{t('dealParams.notFilled')}</p>
        ) : (
          <dl className={styles.digest}>
            {draft.positions.length > 0 && (
              <div>
                <dt>{t('dealParams.positionsTitle')}</dt>
                <dd>
                  {draft.positions.map((p) => `${p.robotType} × ${p.quantity}`).join('; ')}
                </dd>
              </div>
            )}
            {draft.discountMode !== null && (
              <div>
                <dt>{t('dealParams.plateIdle')}</dt>
                <dd>
                  {t(
                    draft.discountMode === 'DISCOUNT'
                      ? 'dealParams.modeDiscount'
                      : 'dealParams.modeFullPrice',
                  )}
                </dd>
              </div>
            )}
            {draft.totalContractCost != null && (
              <div>
                <dt>
                  {t(educational ? 'dealParams.totalLabelSimple' : 'dealParams.totalLabel')}
                </dt>
                <dd className={styles.mono}>{formatMoney(draft.totalContractCost, locale)}</dd>
              </div>
            )}
            {draft.robotCost != null && (
              <div>
                <dt>{t('dealParams.robotLabel')}</dt>
                <dd className={styles.mono}>{formatMoney(draft.robotCost, locale)}</dd>
              </div>
            )}
            {draft.rtcCost != null && (
              <div>
                <dt>{t('dealParams.rtcLabel')}</dt>
                <dd className={styles.mono}>{formatMoney(draft.rtcCost, locale)}</dd>
              </div>
            )}
            {draft.otherCost != null && (
              <div>
                <dt>{t('dealParams.otherLabel')}</dt>
                <dd className={styles.mono}>{formatMoney(draft.otherCost, locale)}</dd>
              </div>
            )}
            {draft.payments.length > 0 && (
              <div>
                <dt>{t('dealParams.paymentsTitle')}</dt>
                <dd>
                  {draft.payments
                    .map(
                      (p) =>
                        `${formatMoney(p.amount, locale)}${
                          p.paidAt ? ` · ${formatDate(p.paidAt, locale)}` : ''
                        }`,
                    )
                    .join('; ')}
                </dd>
              </div>
            )}
            {draft.shipment.length > 0 && (
              <div>
                <dt>{t('dealParams.shipmentsTitle')}</dt>
                <dd>
                  {draft.shipment
                    .map(
                      (s) =>
                        `${s.positionLabel}${
                          s.shipAt ? ` · ${formatDate(s.shipAt, locale)}` : ''
                        }`,
                    )
                    .join('; ')}
                </dd>
              </div>
            )}
            {draft.integrationDates.length > 0 && (
              <div>
                <dt>{t('dealParams.integrationTitle')}</dt>
                <dd>{draft.integrationDates.map((d) => formatDate(d, locale)).join('; ')}</dd>
              </div>
            )}
            {draft.deliveryDate && (
              <div>
                <dt>{t('dealParams.deliveryDateLabel')}</dt>
                <dd>{formatDate(draft.deliveryDate, locale)}</dd>
              </div>
            )}
            {draft.closingAt && (
              <div>
                <dt>{t('dealParams.closingLabel')}</dt>
                <dd>{formatDate(draft.closingAt, locale)}</dd>
              </div>
            )}
          </dl>
        )}
      </section>
    );
  }

  // ── Editor (manufacturer) ────────────────────────────────────────
  return (
    <section aria-label={t('dealParams.title')} className={styles.section}>
      <h3 className={styles.title}>{t('dealParams.title')}</h3>

      {/* Item 1 — «Позиции»: robot type + qty rows, «Добавить позицию». */}
      <div className={styles.subBlock}>
        <h4 className={styles.subTitle}>{t('dealParams.positionsTitle')}</h4>
        {draft.positions.map((row, i) => (
          <div className={styles.rowLine} key={i}>
            <Input
              label={i === 0 ? t('dealParams.positionRobotType') : undefined}
              aria-label={`${t('dealParams.positionRobotType')} ${i + 1}`}
              helperText={undefined}
              value={row.robotType}
              onChange={(e) => patchPosition(i, { robotType: e.target.value })}
              disabled={mutation.isPending}
              className={styles.growField}
            />
            <Input
              type="number"
              min={1}
              step={1}
              label={i === 0 ? t('dealParams.positionQuantity') : undefined}
              aria-label={`${t('dealParams.positionQuantity')} ${i + 1}`}
              helperText={undefined}
              value={row.quantity || ''}
              onChange={(e) =>
                patchPosition(i, { quantity: e.target.value === '' ? 0 : Number(e.target.value) })
              }
              disabled={mutation.isPending}
              className={styles.qtyField}
            />
            <Button
              variant="ghost"
              size="sm"
              iconOnly
              aria-label={t('dealParams.removePosition', { row: i + 1 })}
              leftIcon={<X size={14} />}
              disabled={mutation.isPending}
              onClick={() =>
                setDraft((d) => ({ ...d, positions: d.positions.filter((_, j) => j !== i) }))
              }
            />
          </div>
        ))}
        <Button
          variant="secondary"
          size="sm"
          leftIcon={<Plus size={14} />}
          disabled={mutation.isPending}
          onClick={() =>
            setDraft((d) => ({
              ...d,
              positions: [...d.positions, { robotType: '', quantity: 1 }],
            }))
          }
        >
          {t('dealParams.addPosition')}
        </Button>
      </div>

      {/* Item 2 — the «Скидка / Полная стоимость» plate. */}
      <div className={styles.subBlock}>
        <DiscountPlate
          mode={draft.discountMode}
          disabled={mutation.isPending}
          onPick={(mode) => patch({ discountMode: mode })}
        />
      </div>

      {/* Item 3 — «Стоимость контракта». */}
      <div className={styles.subBlock}>
        <h4 className={styles.subTitle}>{t('dealParams.costTitle')}</h4>
        <Input
          type="number"
          min={0}
          label={t(educational ? 'dealParams.totalLabelSimple' : 'dealParams.totalLabel')}
          value={numValue(draft.totalContractCost)}
          onChange={(e) =>
            patch({ totalContractCost: e.target.value === '' ? null : Number(e.target.value) })
          }
          disabled={mutation.isPending}
          className={styles.costField}
        />
        {educational ? (
          <>
            {/* Учебные — простой вариант: итог + кол-во позиций + дата
                поставки (the model's deliveryDate). «Всё» per the customer. */}
            <div className={styles.countLine}>
              <span>{t('dealParams.positionsCountLabel')}</span>
              <span className={styles.mono}>
                {t('dealParams.positionsCountValue', {
                  count: positionsTotalCount(draft.positions),
                })}
              </span>
            </div>
            <Input
              type="date"
              label={t('dealParams.deliveryDateLabel')}
              value={draft.deliveryDate ? draft.deliveryDate.slice(0, 10) : ''}
              onChange={(e) => patch({ deliveryDate: e.target.value || null })}
              disabled={mutation.isPending}
              className={styles.dateField}
            />
          </>
        ) : (
          <>
            {/* Промышленные — разбивка + аванс/оплата + живая проверка 4.1. */}
            <Input
              type="number"
              min={0}
              label={t('dealParams.robotLabel')}
              helperText={t('dealParams.robotHint')}
              value={numValue(draft.robotCost)}
              onChange={(e) =>
                patch({ robotCost: e.target.value === '' ? null : Number(e.target.value) })
              }
              disabled={mutation.isPending}
              className={styles.costField}
            />
            <Input
              type="number"
              min={0}
              label={t('dealParams.rtcLabel')}
              helperText={t('dealParams.rtcHint')}
              value={numValue(draft.rtcCost)}
              onChange={(e) =>
                patch({ rtcCost: e.target.value === '' ? null : Number(e.target.value) })
              }
              disabled={mutation.isPending}
              className={styles.costField}
            />
            <Input
              type="number"
              min={0}
              label={t('dealParams.otherLabel')}
              helperText={t('dealParams.otherHint')}
              value={numValue(draft.otherCost)}
              onChange={(e) =>
                patch({ otherCost: e.target.value === '' ? null : Number(e.target.value) })
              }
              disabled={mutation.isPending}
              className={styles.costField}
            />
            {showSumHint && (
              <p role="status" className={mismatch ? styles.sumBad : styles.sumOk}>
                {mismatch
                  ? t('dealParams.sumMismatch', {
                      sum: formatMoney(mismatch.sum, locale),
                      total: formatMoney(mismatch.total, locale),
                      diff: formatMoney(Math.abs(mismatch.sum - mismatch.total), locale),
                    })
                  : t('dealParams.sumOk')}
              </p>
            )}
            <h4 className={styles.subTitle}>{t('dealParams.paymentsTitle')}</h4>
            {draft.payments.map((row, i) => (
              <div className={styles.rowLine} key={i}>
                <span className={styles.rowTag}>
                  {i === 0
                    ? t('dealParams.paymentAdvanceLabel')
                    : t('dealParams.paymentPaymentLabel', { row: i })}
                </span>
                <Input
                  type="number"
                  min={0}
                  label={i === 0 ? t('dealParams.paymentAmount') : undefined}
                  aria-label={`${t('dealParams.paymentAmount')} ${i + 1}`}
                  value={numValue(row.amount)}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      payments: d.payments.map((p, j) =>
                        j === i
                          ? { ...p, amount: e.target.value === '' ? NaN : Number(e.target.value) }
                          : p,
                      ),
                    }))
                  }
                  disabled={mutation.isPending}
                  className={styles.costField}
                />
                <Input
                  type="date"
                  label={i === 0 ? t('dealParams.paymentDate') : undefined}
                  aria-label={`${t('dealParams.paymentDate')} ${i + 1}`}
                  value={row.paidAt ? row.paidAt.slice(0, 10) : ''}
                  onChange={(e) =>
                    setDraft((d) => ({
                      ...d,
                      payments: d.payments.map((p, j) =>
                        j === i ? { ...p, paidAt: e.target.value || null } : p,
                      ),
                    }))
                  }
                  disabled={mutation.isPending}
                  className={styles.dateField}
                />
                <Button
                  variant="ghost"
                  size="sm"
                  iconOnly
                  aria-label={t('dealParams.removePayment', { row: i + 1 })}
                  leftIcon={<X size={14} />}
                  disabled={mutation.isPending}
                  onClick={() =>
                    setDraft((d) => ({ ...d, payments: d.payments.filter((_, j) => j !== i) }))
                  }
                />
              </div>
            ))}
            <Button
              variant="secondary"
              size="sm"
              leftIcon={<Plus size={14} />}
              disabled={mutation.isPending}
              onClick={() =>
                setDraft((d) => ({
                  ...d,
                  payments: [...d.payments, { amount: NaN, paidAt: null }],
                }))
              }
            >
              {t('dealParams.addPayment')}
            </Button>
          </>
        )}
      </div>

      {/* Item 4 — «Дата отгрузки» (industrial: per-position rows; the
          educational delivery date lives in the cost block). */}
      {!educational && (
        <div className={styles.subBlock}>
          <h4 className={styles.subTitle}>{t('dealParams.shipmentsTitle')}</h4>
          <datalist id={listId}>
            {positionLabels(draft.positions).map((label) => (
              <option key={label} value={label} />
            ))}
          </datalist>
          {draft.shipment.map((row, i) => (
            <div className={styles.rowLine} key={i}>
              <Input
                list={listId}
                label={i === 0 ? t('dealParams.shipmentPosition') : undefined}
                aria-label={`${t('dealParams.shipmentPosition')} ${i + 1}`}
                placeholder={t('dealParams.shipmentPositionPlaceholder')}
                value={row.positionLabel}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    shipment: d.shipment.map((s, j) =>
                      j === i ? { ...s, positionLabel: e.target.value } : s,
                    ),
                  }))
                }
                disabled={mutation.isPending}
                className={styles.growField}
              />
              <Input
                type="date"
                label={i === 0 ? t('dealParams.shipmentDate') : undefined}
                aria-label={`${t('dealParams.shipmentDate')} ${i + 1}`}
                value={row.shipAt ? row.shipAt.slice(0, 10) : ''}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    shipment: d.shipment.map((s, j) =>
                      j === i ? { ...s, shipAt: e.target.value || null } : s,
                    ),
                  }))
                }
                disabled={mutation.isPending}
                className={styles.dateField}
              />
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={t('dealParams.removeShipment', { row: i + 1 })}
                leftIcon={<X size={14} />}
                disabled={mutation.isPending}
                onClick={() =>
                  setDraft((d) => ({ ...d, shipment: d.shipment.filter((_, j) => j !== i) }))
                }
              />
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus size={14} />}
            disabled={mutation.isPending}
            onClick={() =>
              setDraft((d) => ({
                ...d,
                shipment: [...d.shipment, { positionLabel: '', shipAt: null }],
              }))
            }
          >
            {t('dealParams.addShipment')}
          </Button>
        </div>
      )}

      {/* Item 5 — «Дата интеграции РТК» (non-blocking; industrial — РТК is
          the integration-deal concept). */}
      {!educational && (
        <div className={styles.subBlock}>
          <h4 className={styles.subTitle}>{t('dealParams.integrationTitle')}</h4>
          <p className={styles.hintLine}>{t('dealParams.integrationNotBlocking')}</p>
          {draft.integrationDates.map((date, i) => (
            <div className={styles.rowLine} key={i}>
              <Input
                type="date"
                label={i === 0 ? t('dealParams.integrationTitle') : undefined}
                aria-label={`${t('dealParams.integrationTitle')} ${i + 1}`}
                value={date ? date.slice(0, 10) : ''}
                onChange={(e) =>
                  setDraft((d) => ({
                    ...d,
                    integrationDates: d.integrationDates.map((v, j) =>
                      j === i ? e.target.value : v,
                    ),
                  }))
                }
                disabled={mutation.isPending}
                className={styles.dateField}
              />
              <Button
                variant="ghost"
                size="sm"
                iconOnly
                aria-label={t('dealParams.removeIntegrationDate', { row: i + 1 })}
                leftIcon={<X size={14} />}
                disabled={mutation.isPending}
                onClick={() =>
                  setDraft((d) => ({
                    ...d,
                    integrationDates: d.integrationDates.filter((_, j) => j !== i),
                  }))
                }
              />
            </div>
          ))}
          <Button
            variant="secondary"
            size="sm"
            leftIcon={<Plus size={14} />}
            disabled={mutation.isPending}
            onClick={() =>
              setDraft((d) => ({ ...d, integrationDates: [...d.integrationDates, ''] }))
            }
          >
            {t('dealParams.addIntegrationDate')}
          </Button>
        </div>
      )}

      {/* Item 6 — «Дата закрытия сделки» (non-blocking, both product lines). */}
      <div className={styles.subBlock}>
        <Input
          type="date"
          label={t('dealParams.closingLabel')}
          helperText={t('dealParams.closingNotBlocking')}
          value={draft.closingAt ? draft.closingAt.slice(0, 10) : ''}
          onChange={(e) => patch({ closingAt: e.target.value || null })}
          disabled={mutation.isPending}
          className={styles.dateField}
        />
      </div>

      {formError && (
        <p role="alert" className={styles.formError}>
          {t(`dealParams.${formError}`)}
        </p>
      )}
      <div className={styles.saveRow}>
        <Button
          variant="secondary"
          size="sm"
          disabled={!dirty || mutation.isPending}
          loading={mutation.isPending}
          onClick={save}
        >
          {t('common.save')}
        </Button>
      </div>
    </section>
  );
}
